/* eslint-disable class-methods-use-this */
import { Requests } from '@nx-smartmonkey/shared/helpers';
import { CountryCode } from 'libphonenumber-js';

import { Platform } from '../../application/CapacitorService';
import { CookiesConstants } from '../../constants/Cookies';
import { CookiesInterface } from '../../services/cookies/cookies';
import { OTP_METHOD } from './';
import { LoginProvider, RegisterDTO, SessionRepository } from './SessionRepository';

export class SessionRepositoryApi implements SessionRepository {
  constructor(private requests: Requests, private cookies: CookiesInterface) {}

  async isLogged(): Promise<boolean> {
    return await this.cookies.hasCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME);
  }

  async getToken(): Promise<string | undefined> {
    return await this.cookies.getCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME);
  }

  async getAuthenticationHeader(): Promise<Record<string, any>> {
    const token = await this.cookies.getCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async generateOtpCode({
    phone,
    otpMethod,
  }: {
    phone: string;
    otpMethod: OTP_METHOD;
    tokenValidator: {
      data: string;
      signature: string;
    };
    platform: Platform;
  }): Promise<string> {
    const response = await this.requests.get({
      uri: `/drivers/v3/session/login/phone/generate`,
      params: {
        phone,
        otp_method: otpMethod,
      },
    });

    const { token } = response.data;

    await this.cookies.setCookie(CookiesConstants.PHONE_COOKIE_NAME, token, 360);
    await this.cookies.deleteCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME);

    return token;
  }

  async verifyLogin({ otpCode }: { otpCode: string }): Promise<string> {
    const token = await this.cookies.getCookie(CookiesConstants.PHONE_COOKIE_NAME);

    if (!token) {
      throw new Error(`Could not verify session token, no phone token found`);
    }

    const response = await this.requests.get({
      uri: `/drivers/v3/session/login/phone/validate`,
      params: { token, otp_code: otpCode },
    });

    const { token: verifiedToken } = response.data;

    await this.cookies.setCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME, verifiedToken, 360);

    return verifiedToken;
  }

  async loginProvider({
    provider,
    providerToken,
    accessToken,
  }: {
    provider: LoginProvider;
    providerToken: string;
    accessToken: string;
  }): Promise<string> {
    const response = await this.requests.get({
      uri: `/drivers/v3/session/login/provider`,
      params: { provider_token: providerToken, provider, access_token: accessToken },
    });

    const { token } = response.data;

    await this.cookies.setCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME, token, 360);

    return token;
  }

  async createRegisterToken(props: RegisterDTO): Promise<string> {
    const {
      providerToken,
      provider,
      accessToken,
      language,
      organizationName: organization_name,
      workSchedule: work_schedule,
      timeZone: time_zone,
      vehicleType: transport_mode,
      referralId: referral_id,
      utmId: utm_id,
      utmCampaign: utm_campaign,
      utmContent: utm_content,
      utmGclid: utm_gclid,
      utmMedium: utm_medium,
      utmSource: utm_source,
      utmTerm: utm_term,
    } = props;
    const response = await this.requests.post({
      uri: `/drivers/v3/session/register/provider`,
      data: {
        provider,
        provider_token: providerToken,
        access_token: accessToken,
        register_data: {
          language,
          organization_name,
          work_schedule,
          time_zone,
          transport_mode,
          referral_id,
          utm_id,
          utm_campaign,
          utm_content,
          utm_gclid,
          utm_medium,
          utm_source,
          utm_term,
        },
      },
    });

    const { token: verifiedToken } = response.data;

    await this.cookies.setCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME, verifiedToken, 360);

    return verifiedToken;
  }

  async createSessionMagicLink({ vehicleId, routeId }: { vehicleId: string; routeId: string }): Promise<string> {
    const response = await this.requests.get({
      uri: `/drivers/v3/session/validate_link`,
      params: { vehicle_id: vehicleId, route_id: routeId },
    });

    const { token, route_id } = response.data;

    await this.cleanSessionParameters();
    await this.cookies.setCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME, token, 360);

    return route_id;
  }

  async cleanSessionParameters(): Promise<void> {
    await this.cookies.deleteCookie(CookiesConstants.PHONE_COOKIE_NAME);
    await this.cookies.deleteCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME);
  }

  async deleteSessionToken(): Promise<void> {
    try {
      const token = await this.cookies.getCookie(CookiesConstants.SM_DRIVERS_COOKIE_NAME);

      if (!token) {
        await this.cleanSessionParameters();
        return;
        // throw new Error(`Could not logout: invalid session token`);
      }

      const response = await this.requests.get({
        uri: `/drivers/v3/session/logout`,
        params: {},
        headers: {
          ...(token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {}),
        },
      });

      await this.cleanSessionParameters();
      return response.data.token;
    } catch {
      await this.cleanSessionParameters();
    }
  }

  async getCountry(): Promise<CountryCode> {
    const defaultCountryCode: CountryCode = `ES`;
    const requests = Requests.create(`https://get.geojs.io`);
    const response = await requests.get({ uri: `/v1/ip/geo.json` });

    if (response.status !== 200) {
      return defaultCountryCode;
    }

    const { country_code } = response.data ?? {};
    return (country_code as CountryCode) ?? defaultCountryCode;
  }
}
